import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pager, ConfirmModal, PageSpinner } from '@/components/common';
import { useCustomSurveys, usePatchSurveyStatus } from '@/hooks/useSurveys';
import type { CustomSurvey } from '@/types/survey.types';
import { SurveyStatusBadge } from '../../components/SurveyStatusBadge';
import { ColFilterPopover, FltBtn } from '../../components/ColFilterPopover';
import { TrashModal } from '../../components/TrashModal';
import type { ActiveFilterState } from '../../components/ColFilterPopover';
import type { TrashItem } from '@/types/survey.types';
import type { ConfirmModalConfig } from '@/types/common.types';

function formatDate(iso: string, lang: string) {
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' });
}

interface CustomTableProps {
  search: string;
  hidden: Set<string>;
  onOpenBuilder: (m: { mode: 'create' } | { mode: 'edit'; surveyId: string }) => void;
  onDuplicate?: (id: string) => void;
  onExportExcel?: (id: string) => void;
  deletedIds: Set<string>;
  trash: TrashItem[];
  onMoveToTrash: (id: string, name: string, responses: number) => void;
  onRestoreFromTrash: (id: string) => void;
  onDeleteForever: (id: string) => void;
  showTrash: boolean;
  onCloseTrash: () => void;
  /** Optional surveys override — if omitted, falls back to mock data */
  surveys?: CustomSurvey[];
  /** Whether any survey mutation (delete/restore/duplicate) is in flight */
  mutationPending?: boolean;
}

export function CustomTable({
  search,
  hidden,
  onOpenBuilder,
  onDuplicate,
  onExportExcel,
  deletedIds,
  trash,
  onMoveToTrash,
  onRestoreFromTrash,
  onDeleteForever,
  showTrash,
  onCloseTrash,
  surveys: surveysProp,
  mutationPending = false,
}: CustomTableProps) {
  const { t, i18n } = useTranslation();
  const { data: fetchedSurveys, isError, isLoading } = useCustomSurveys();
  const SURVEYS: CustomSurvey[] = (surveysProp ?? fetchedSurveys ?? []) as CustomSurvey[];
  const patchStatus = usePatchSurveyStatus();

  const [surveyStatuses, setSurveyStatuses] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [activeFilter, setActiveFilter] = useState<ActiveFilterState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [confirmOpts, setConfirmOpts] = useState<ConfirmModalConfig | null>(null);

  function getStatus(s: { id: string; status: string }) { return surveyStatuses[s.id] ?? s.status; }
  function toggleStatus(id: string, currentStatus: string) {
    const next = currentStatus === 'active' ? 'paused' : 'active';
    setSurveyStatuses(prev => ({ ...prev, [id]: next }));
    patchStatus.mutate({ id, action: currentStatus === 'active' ? 'pause' : 'activate' });
  }

  function setFilter(col: string, val: string) {
    setFilters(prev => ({ ...prev, [col]: val }));
    setPage(1);
  }

  function openFilter(col: string, e: React.MouseEvent<HTMLButtonElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveFilter(prev => prev?.col === col ? null : { col, top: rect.bottom + 4, left: rect.left });
  }

  const active = useMemo(() => {
    const f = filters;
    return SURVEYS.filter(s => {
      if (deletedIds.has(s.id)) return false;
      if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (f.name && !s.name.toLowerCase().includes(f.name.toLowerCase())) return false;
      if (f.createdBy && !s.createdBy.toLowerCase().includes(f.createdBy.toLowerCase())) return false;
      if (f.status && s.status !== f.status) return false;
      if (f.audience && s.audience !== f.audience) return false;
      return true;
    });
  }, [search, deletedIds, filters, SURVEYS]);

  const sliced = useMemo(() => active.slice((page - 1) * pageSize, page * pageSize), [active, page, pageSize]);

  const flt = (col: string) => ({
    col,
    active: activeFilter?.col === col,
    hasFilter: !!(filters[col]),
    onOpen: openFilter,
  });

  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    return (
      <div className="tips-empty">
        <i className="fas fa-exclamation-circle" />
        <p>{t('admin.surveysError')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="surveys-table">
        <div className="surveys-table-scroll">
          <table>
            <thead>
              <tr>
                {!hidden.has('name')      && <th><span className="col-flt-th">{t('admin.colName')}<FltBtn {...flt('name')} /></span></th>}
                {!hidden.has('createdBy') && <th><span className="col-flt-th">{t('admin.colCreatedBy')}<FltBtn {...flt('createdBy')} /></span></th>}
                {!hidden.has('status')    && <th><span className="col-flt-th">{t('admin.colStatus')}<FltBtn {...flt('status')} /></span></th>}
                {!hidden.has('audience')  && <th><span className="col-flt-th">{t('admin.colAudience')}<FltBtn {...flt('audience')} /></span></th>}
                {!hidden.has('questions') && <th>{t('admin.colQuestions')}</th>}
                {!hidden.has('feedback')  && <th>{t('admin.colFeedback')}</th>}
                {!hidden.has('period')    && <th>{t('admin.colPeriod')}</th>}
                {!hidden.has('actions')   && <th>{t('admin.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {!isLoading && sliced.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    <i className="fas fa-inbox" style={{ marginRight: 8 }} />
                    {t('admin.surveysNoResults')}
                  </td>
                </tr>
              )}
              {sliced.map(s => (
                <tr key={s.id}>
                  {!hidden.has('name') && (
                    <td>
                      <strong>{s.name}</strong>
                      {s.status === 'active' && (
                        <span className="anon-badge"><i className="fas fa-user-secret" /> {t('admin.anonymous')}</span>
                      )}
                    </td>
                  )}
                  {!hidden.has('createdBy') && <td><span className="adm-created-by">{s.createdBy}</span></td>}
                  {!hidden.has('status')    && <td><SurveyStatusBadge status={getStatus(s)} /></td>}
                  {!hidden.has('audience') && (
                    <td>
                      {s.audienceType === 'meeting' && t('admin.audienceTypeMeeting')}
                      {s.audienceType === 'people'  && t('admin.audienceTypePeople')}
                      {s.audienceType === 'ou'      && t('admin.audienceTypeOU')}
                      {!s.audienceType && (s.audience || '—')}
                    </td>
                  )}
                  {!hidden.has('questions') && <td><span className="question-count">{s.questions}</span></td>}
                  {!hidden.has('feedback')  && (
                    <td>
                      <div className="mini-progress">
                        <span>{s.responses}</span>
                        <div className="mini-bar"><div style={{ width: `${Math.min(100, (s.responses / 50) * 100)}%` }} /></div>
                      </div>
                    </td>
                  )}
                  {!hidden.has('period') && (
                    <td>
                      {s.periodStart || s.periodEnd
                        ? <>
                            {s.periodStart && <>{formatDate(s.periodStart, i18n.language)} → </>}
                            {s.periodEnd && <strong>{formatDate(s.periodEnd, i18n.language)}</strong>}
                          </>
                        : s.period || '—'}
                    </td>
                  )}
                  {!hidden.has('actions') && (
                    <td>
                      <div className="adm-actions">
                        <button className="btn-icon" title={t('common.edit')} onClick={() => onOpenBuilder({ mode: 'edit', surveyId: s.id })}>
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          className="btn-icon"
                          title={getStatus(s) === 'active' ? t('admin.pauseSurvey') : t('admin.activateSurvey')}
                          onClick={() => toggleStatus(s.id, getStatus(s))}
                        >
                          <i className={`fas fa-${getStatus(s) === 'active' ? 'pause' : 'play'}`} />
                        </button>
                        <button className="btn-icon" title={t('admin.duplicate')} disabled={mutationPending} onClick={() => onDuplicate?.(s.id)}>
                          {mutationPending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-copy" />}
                        </button>
                        <button className="btn-icon success" title={t('admin.exportExcelTip')} onClick={() => onExportExcel?.(s.id)} disabled={!s.responses}><i className="fas fa-file-excel" /></button>
                        <button
                          className="btn-icon danger"
                          title={t('common.delete')}
                          disabled={mutationPending}
                          onClick={() => setConfirmOpts({
                            title: t('admin.confirmMoveToTrash'),
                            message: t('admin.confirmMoveToTrashMsg'),
                            confirmText: t('admin.confirmMoveBtn'),
                            onConfirm: () => onMoveToTrash(s.id, s.name, s.responses),
                          })}
                        >
                          {mutationPending ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-trash" />}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager
          total={active.length}
          page={page}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={s => { setPageSize(s); setPage(1); }}
        />
      </div>

      {activeFilter && (
        <ColFilterPopover
          col={activeFilter.col}
          top={activeFilter.top}
          left={activeFilter.left}
          filters={filters}
          onFilter={setFilter}
          onClose={() => setActiveFilter(null)}
        />
      )}

      {confirmOpts && (
        <ConfirmModal
          title={confirmOpts.title}
          message={confirmOpts.message}
          confirmText={confirmOpts.confirmText}
          onConfirm={confirmOpts.onConfirm}
          onClose={() => setConfirmOpts(null)}
        />
      )}

      {showTrash && (
        <TrashModal
          items={trash}
          onRestore={onRestoreFromTrash}
          onDeleteForever={onDeleteForever}
          onClose={onCloseTrash}
        />
      )}
    </>
  );
}
